'use client';
import {lazy, Suspense, useEffect, useState} from "react";
import dynamic from "next/dynamic";

import {Sheet, SheetContent, SheetTrigger} from "@/shadcn/components/ui/sheet";
import {Button} from "@/shadcn/components/ui/button";

import {LogOut, Menu, UserRound} from "lucide-react";
import {useRouter} from "next-nprogress-bar";
import {DialogTitle} from "@/shadcn/components/ui/dialog";
import {VisuallyHidden} from "@radix-ui/react-visually-hidden";
import {usePathname} from "next/navigation";
import {Category} from "@/types";
import {authClient} from "@/lib/auth-client";
const AuthenticationSheet = lazy(() => import("@/components/auth/authentication-sheet"));
import {FavoriteListButton} from "@/components/favorites/favorite-list-button";
import {DialogDescription} from "@radix-ui/react-dialog";

// Lazy-load MobileMenuClient to defer framer-motion (~34KB) until menu is opened
const MobileMenuClient = dynamic(
    () => import('./mobile-menu.client').then(mod => ({default: mod.MobileMenuClient})),
    {
        ssr: false,
        loading: () => (
            <div className="flex flex-col h-[65dvh] p-4 animate-pulse">
                <div className="flex justify-center mb-3">
                    <div className="h-8 w-32 bg-gray-200 rounded" />
                </div>
                <div className="flex gap-2 mb-4">
                    <div className="h-10 flex-1 bg-gray-200 rounded" />
                    <div className="h-10 flex-1 bg-gray-200 rounded" />
                </div>
                <div className="space-y-2">
                    {[...Array(8)].map((_, i) => (
                        <div key={i} className="h-12 bg-gray-200 rounded" />
                    ))}
                </div>
            </div>
        )
    }
);


export const MobileMenuSheetWrapper = ({initialCategories}: { initialCategories: Category[] }) => {
    const {data, error, isPending, refetch} = authClient.useSession();
    const {push} = useRouter()
    const pathname = usePathname();
    const [isSheetOpen, setIsSheetOpen] = useState(false);

    useEffect(() => {
        setIsSheetOpen(false);
    }, [pathname]);

    // (D) Close entire sheet
    const closeSheet = () => {
        setIsSheetOpen(false);
    };

    const logout = async () => {
        await authClient.signOut({
            fetchOptions: {
                onSuccess: () => {
                    refetch()
                },
            },
        })
    }

    return (
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
            {/* Button that triggers opening the sheet */}
            <SheetTrigger asChild>
                <Button variant={'outline'} size={'icon'} className="-mt-4 relative flex-col items-center justify-center focus:ring-0">
                    <Menu className="h-5 w-5"/>
                    <span className="absolute mt-14 text-[0.65rem]">Menu</span>
                </Button>
            </SheetTrigger>

            <SheetContent side="left" className="overflow-hidden p-0 pt-2 h-[100dvh] w-full max-w-96 flex flex-col pb-20">
                <DialogDescription/>
                <VisuallyHidden>
                    <DialogTitle></DialogTitle>
                </VisuallyHidden>
                <MobileMenuClient initialCategories={initialCategories} closeSheet={closeSheet}/>
                <div
                    className="absolute bottom-0 left-0 right-0 h-20 flex flex-col justify-center w-full  z-50 bg-white ">
                    <div className="absolute top-0 left-2 right-2 border-t border-t-slate-200 -pt-8"></div>
                    <div className="flex gap-4 items-center justify-evenly">

                        {data && <Button
                            variant={'outline'} size={'icon'}
                            onClick={() => {
                                closeSheet();
                                push("/konto");
                            }}
                            className={'sm:hidden -mt-4 relative  flex-col items-center justify-center focus:ring-0 lg:flex-row gap-0.5 lg:gap-2'}>
                            <UserRound className={"h-5 w-5 "}/>
                            <span className={"absolute mt-14 text-[0.65rem]"}>Konto</span>
                        </Button>}
                        {!data && <Suspense fallback={null}><AuthenticationSheet/></Suspense>}
                        <FavoriteListButton onClose={closeSheet}/>
                        {data && <Button
                            variant={'outline'} size={'icon'}
                            onClick={() => {
                                logout();
                                closeSheet()
                            }}
                            className={'sm:hidden -mt-4 relative  flex-col items-center justify-center focus:ring-0 lg:flex-row gap-0.5 lg:gap-2'}>
                            <LogOut className="h-5 w-5"/>
                            <span className={"absolute mt-14 text-[0.65rem]"}>Logout</span>
                        </Button>}
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};
